/* import __COLOCATED_TEMPLATE__ from './reason-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { ChartSeriesDefinition } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { inject as service } from '@ember/service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { action } from '@ember/object';
import { and } from 'embercom/components/reporting/bespoke/fin-performance/conversation-funnel/series-config';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';

interface Signature {
  Args: {
    job: Job;
    reportState: ReportState;
    selectedJobId: string;
    onOpenSectionChange: (jobId: string) => void;
    taskSuggestions?: TaskSuggestion[];
  };
}

export default class ReasonCard extends Component<Signature> {
  @service declare reportingMetrics: ReportingMetrics;
  @service declare contentImportService: any;

  @use metricResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  get isLoading() {
    return this.metricResource.isLoading;
  }

  get chartDefinition() {
    let filters = this.args.job.filters;

    if (this.args.job.chartSeries[0].filters) {
      filters = and(this.args.job.chartSeries[0].filters, this.args.job.filters);
    } else {
      filters = this.args.job.filters;
    }

    return {
      chart_series: this.args.job.chartSeries.map((series: ChartSeriesDefinition) => ({
        ...series,
        filters,
      })),
      view_by: null,
    };
  }

  get suggestionCount() {
    if (this.args.job.id === 'dataGaps') {
      return this.dataGapsSuggestions;
    } else if (this.args.job.id === 'actionGaps') {
      return this.actionGapsSuggestions;
    }
    return this.contentImportService.aiContentReviewSummary.pending;
  }

  get dataGapsSuggestions(): number {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions.filter((suggestion) => suggestion.isDataGap).length;
  }

  get actionGapsSuggestions(): number {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions.filter((suggestion) => suggestion.isActionGap).length;
  }

  get metricValue() {
    let metricValue = this.metricResource.kpiData[0];
    return this.metricResource.formatValue({
      value: metricValue,
      durationFormat: 'hours',
    });
  }

  @action
  handleSectionChange(jobId: string) {
    if (jobId === this.args.selectedJobId) {
      return;
    }
    this.args.onOpenSectionChange?.(jobId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::ReasonCard': typeof ReasonCard;
    'reporting/automation/ai-insights/optimize/reason-card': typeof ReasonCard;
  }
}
